<template>
  <DialogWrapper
    class="popup-policy-dialog"
    width="40%"
    hasCustomTitle
    hiddenHeaderLine
    :show.sync="isShow"
    :isMobile="isMobile"
  >
    <div class="policy-content">
      <div class="policy-title">
        <div v-if="!hasCustomer" class="title">
          <div v-for="(item, index) in header" class="policy-title-item" :key="index">
            <div v-html="item.i18nContent"></div>
          </div>
        </div>
        <div v-else-if="hasCustomer && companyPolicyInfo.link" class="title">
          <span>{{ companyPolicyInfo.companyName }}</span>
        </div>
      </div>
      <div v-if="!onlySystemPolicy" class="policy-body">
        <el-form class="policy-form" ref="policyForm" :model="agreedPolicy" :rules="formRules">
          <el-form-item class="policy-item" v-for="(policy, key) in bodyContent" :key="key" :prop="`policy_${policy.id}`">
            <span v-if="policy.type==='CONTENT'" v-html="policy.i18nContent" class="policy-type-content"></span>
            <el-checkbox v-if="policy.type==='CHECKBOX'" v-model="agreedPolicy[`policy_${policy.id}`]" class="checkbox" :key="policy.id">
              <span v-html="policy.i18nContent"></span>
            </el-checkbox>
          </el-form-item>
        </el-form >
      </div>
      <div v-if="showSystemPolicy" class="system-policy" :class="(onlySystemPolicy || !bodyContent) && 'is-only-system-policy'">
        <div class="policy-item">
          <el-checkbox v-model="agreeSystemPolicy" class="checkbox" @change="handleSystemPolicyChange">
            {{ $t('common.label.agreePolicy') }}
            <a class="policy-link" href="/tos/" target="_blank">{{ $t('login.company.register.candidate.policy.link1') }}</a>
            &
            <a class="policy-link" href="/policy/" target="_blank">{{ $t('login.company.register.candidate.policy.link2') }}</a>
          </el-checkbox>
        </div>
        <span v-if="showSystemPolicyWarning" class="system-policy-warning">{{ $t('common.message.agreeSystemPolicyWarning') }}</span>
      </div>
    </div>
    <template #footer>
      <div class="footer">
        <el-button class="footer-btn cancel-btn" @click="handleCancelBtnClick">{{ cancelBtn }}</el-button>
        <el-button class="footer-btn save-btn" @click="handleAgreeBtnClick">{{ confirmBtn }}</el-button>
      </div>
    </template>
  </DialogWrapper>
</template>

<script>
import _ from 'lodash'
import { addI18nProperty } from '@/i18n'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'

export default {
  components: {
    DialogWrapper
  },
  props: {
    companyPolicyInfo: {
      required: true
    },
    isMobile: {
      type: Boolean,
      default: false
    },
    titleI18nKey: {
      type: String,
      default: ''
    },
    show: {
      type: Boolean,
      default: false
    },
    showSystemPolicy: {
      type: Boolean,
      default: false
    },
    isAgreeSystemPolicy: {
      type: Boolean,
      default: false
    },
    onlySystemPolicy: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      agreedPolicy: {},
      agreeSystemPolicy: false,
      showSystemPolicyWarning: false
    }
  },
  computed: {
    isShow: {
      get () {
        return this.show
      },
      set (val) {
        this.$emit('update:show', val)
      }
    },
    hasCustomer () {
      return this.companyPolicyInfo.has_customer
    },
    hasColorCustomization () {
      return this.companyPolicyInfo.company_has_color_customization
    },
    customColor () {
      return this.companyPolicyInfo.company_custom_color ? this.companyPolicyInfo.company_custom_color : '#fff'
    },
    header () {
      if (this.companyPolicyInfo.popup) {
        for (const header of this.companyPolicyInfo.popup.header) {
          addI18nProperty(header, 'i18nContent', 'content_cn', 'content_en')
        }
        return this.companyPolicyInfo.popup.header
      }
      return null
    },
    defaultHeader () {
      if (this.companyPolicyInfo.popup && this.companyPolicyInfo.popup.customer_header) {
        return this.companyPolicyInfo.popup.customer_header
      } else if (this.companyPolicyInfo.link && this.companyPolicyInfo.customer_header) {
        const companyLogoImg = this.hasCustomer ? `<img src="${this.companyPolicyInfo.customer_header}" class="policy-logo" style="height: 70px">` : this.companyPolicyInfo.customer_header
        return companyLogoImg
      }
      if (this.hasCustomer && this.companyPolicyInfo.popup) {
        return this.header[0]?.i18nContent
      }
      return null
    },
    bodyContent () {
      if (this.companyPolicyInfo.popup) {
        _.forEach(this.companyPolicyInfo.popup.body, (policy) => {
          addI18nProperty(policy, 'i18nContent', 'content_cn', 'content_en')
        })
        return this.companyPolicyInfo.popup.body
      }
      _.forEach(this.companyPolicyInfo.link, (link) => {
        addI18nProperty(link, 'i18nContent', 'content_cn', 'content_en')
      })
      return this.companyPolicyInfo.link
    },
    cancelBtn () {
      let cancelContent = null
      if (this.companyPolicyInfo.popup) {
        _.forEach(this.companyPolicyInfo.popup.footer, (footerBtn) => {
          if (footerBtn.type === 'BUTTON_CANCEL') {
            addI18nProperty(footerBtn, 'i18nCancelBtn', 'content_cn', 'content_en')
            cancelContent = footerBtn.i18nCancelBtn
          }
        })
      }
      if (cancelContent) {
        return cancelContent
      }
      return this.$t('common.btn.cancel')
    },
    confirmBtn () {
      let confirmContent = null
      if (this.companyPolicyInfo.popup) {
        _.forEach(this.companyPolicyInfo.popup.footer, (footerBtn) => {
          if (footerBtn.type === 'BUTTON_SAVE') {
            addI18nProperty(footerBtn, 'i18nSaveBtn', 'content_cn', 'content_en')
            confirmContent = footerBtn.i18nSaveBtn
          }
        })
      }
      if (confirmContent) {
        return confirmContent
      }
      return this.$t('common.btn.save')
    },
    formRules () {
      const validateCheckBox = (rule, value, callback) => {
        if (!value) {
          callback(new Error(this.$t('common.message.continueWithAgreePolicy')))
        } else {
          callback()
        }
      }
      const rules = {}
      const checkboxRequired = [
        {
          validator: validateCheckBox,
          trigger: 'change'
        }
      ]
      _.forEach(this.bodyContent, (policy) => {
        if (policy.is_required) {
          rules['policy_' + policy.id] = checkboxRequired
        }
      })
      return rules
    }
  },
  created () {
    this.formatAgreementStatus()
  },
  methods: {
    formatAgreementStatus () {
      // 初始化协议同意状态
      _.forEach(this.bodyContent, (policy) => {
        this.$set(this.agreedPolicy, `policy_${policy.id}`, policy.is_agree)
      })
    },
    handleSystemPolicyChange () {
      if (!this.agreeSystemPolicy) {
        this.showSystemPolicyWarning = true
      } else {
        this.showSystemPolicyWarning = false
      }
    },
    handleCancelBtnClick () {
      this.$emit('cancel')
    },
    async handleAgreeBtnClick () {
      let validCompanyPolicy = false
      let validSystemPolicy = !this.showSystemPolicy
      if (this.onlySystemPolicy) {
        validCompanyPolicy = true
      } else {
        this.$refs.policyForm.validate((valid) => {
          validCompanyPolicy = valid
        })
      }
      if (this.showSystemPolicy) {
        if (!this.agreeSystemPolicy) {
          this.showSystemPolicyWarning = true
          validSystemPolicy = false
        } else {
          this.showSystemPolicyWarning = false
          validSystemPolicy = true
        }
      }
      if (validCompanyPolicy && validSystemPolicy) {
        const policyItemIds = []
        _.forEach(this.agreedPolicy, (val, key) => {
          if (val && key.startsWith('policy_')) {
            policyItemIds.push(_.parseInt(key.substring(7)))
          }
        })
        this.$emit('save', {
          policyItemIds,
          isAgreedSystemPolicy: this.showSystemPolicy ? this.agreeSystemPolicy : undefined
        })
      }
    }
  },
  watch: {
    isAgreeSystemPolicy: {
      immediate: true,
      handler (val) {
        this.agreeSystemPolicy = val
      }
    }
  }
}
</script>

<style scoped lang="scss">
.popup-policy-dialog {
  .policy-header {
    margin-top: 7px;
    margin-bottom: 25px;

    .policy-logo-content {
      height: 60px;
    }

    .policy-logo-content-no-customer {
      height: 60px;
    }
  }
  .policy-content {
    min-height: 280px;
    display: flex;
    flex-direction: column;

    .policy-title {
      .title {
        font-weight: bold;
        font-size: 14px;
        text-align: center;
        line-height: normal;
        word-break: break-word;
      }
    }

    .policy-body {
      .policy-form {
        .policy-item {
          .policy-type-content {
            display: block;
            margin-top: 10px;
            margin-bottom: 10px;
          }

          &:last-child {
            margin-bottom: 10px;
          }
        }

        .checkbox {
          display: block;
          margin-top: 20px;
          ::v-deep .el-checkbox__label {
            white-space: pre-wrap;
            vertical-align: top;
            width: 90%;
            word-break: break-word;
          }

          ::v-deep .el-checkbox__input {
            vertical-align: top;
            margin-top: 1px;
          }

          span {
            color: black;

            &.error-message {
              display: none;
              color: red;
            }
          }
        }

        ::v-deep {
          p {
            margin: 0;
          }

          a {
            color: #0096cc;
          }

          .el-form-item {
            margin-bottom: auto;
          }

          .el-form-item__content {
            display: flex;
            flex-direction: column;
            line-height: unset;

            .el-form-item__error {
              left: 22px;
              position:relative;
              color: #F56C6C;
            }

          }
        }
      }
    }

    .system-policy {
      margin-top: auto;

      &.is-only-system-policy {
        margin-top: 10px;
      }

      .policy-item {
        a {
          color: #0096cc;
        }

        ::v-deep {
          .el-checkbox {
            overflow: hidden;
            white-space: normal;
            display: flex;
            justify-content: flex-start;

            .el-checkbox__input {
              margin-top: 2px;
            }
          }
        }
      }

      .system-policy-warning {
        color: #F56C6C;
        font-size: 12px;
        line-height: 1;
        padding-top: 4px;
        position: absolute;
        top: 100%;
        left: 0;
        left: 22px;
        position: relative;
      }
    }
  }
  .footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    .footer-btn {
      font-size: 14px;
      border: 0;
      cursor: pointer;
      margin-left: 24px;
      line-height: 14px;
      padding: 10px 30px;
      background: var(--custom-color);
      color: #fff;

      &:first-child {
        margin-left: 0;
        background: #a9a9a9;
        color: #fff;
        border: 1px solid #a9a9a9;
      }

      &:last-child {
        background: var(--custom-color);
        color: #fff;
        border: 1px solid var(--custom-color);
      }
    }
  }

  &.is-mobile {
    .el-dialog {
      .el-dialog__footer {
        .footer {
          align-items: stretch;

          .footer-btn {
            flex: 1 1 0;
            min-width: 0;
            min-height: 30px;
            padding: 10px 0;
            white-space: pre-wrap;
            word-break: break-word;
          }
        }
      }
    }

    .system-policy {
      margin-bottom: 10px;
    }
  }
}
</style>
